import { deleteSessionDataAtom } from '@/components/modal/delete-session-modal.atoms';
import { deleteTranscriptDataAtom } from '@/components/modal/delete-transcript-modal.atoms';
import {
  isBulkCreatingSessionsAtom,
  isUploadScheduleUnsavedAlertDialogOpenAtom,
  isUploadSessionScheduleModalOpenAtom,
} from '@/features/uploadSessionSchedule/atoms';
import { useBeforeUnloadWarning } from '@/features/uploadSessionSchedule/hooks/useBeforeUnloadWarning';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { useState } from 'react';

// App composition owns lazy mounting; existing domain implementations keep their
// established paths and mutation dependencies.
const DeleteSessionModalImplementation = dynamic(
  () => import('@/components/modal/delete-session-modal'),
  {
    ssr: false,
    loading: ({ error }) => {
      if (error) throw error;
      return null;
    },
  }
);

export function DeleteSessionModal() {
  const isOpen = Boolean(useAtomValue(deleteSessionDataAtom));
  const [isDeleting, setIsDeleting] = useState(false);
  // A closed dialog may still own an in-flight mutation. Retain its observer
  // so reopening cannot enable a second delete before the first settles.
  if (!isOpen && !isDeleting) return null;
  return <DeleteSessionModalImplementation onPendingChange={setIsDeleting} />;
}

const DeleteTranscriptModalImplementation = dynamic(
  () => import('@/components/modal/delete-transcript-modal'),
  {
    ssr: false,
    loading: ({ error }) => {
      if (error) throw error;
      return null;
    },
  }
);

export function DeleteTranscriptModal() {
  const isOpen = Boolean(useAtomValue(deleteTranscriptDataAtom));
  const [isDeleting, setIsDeleting] = useState(false);
  // A closed dialog may still own an in-flight mutation. Retain its observer
  // so reopening cannot enable a second delete before the first settles.
  if (!isOpen && !isDeleting) return null;
  return <DeleteTranscriptModalImplementation onPendingChange={setIsDeleting} />;
}

const UploadSessionScheduleModalImplementation = dynamic(
  () =>
    import('@/features/uploadSessionSchedule/components/upload-session-schedule-modal').then(
      (mod) => mod.UploadSessionScheduleModal
    ),
  {
    ssr: false,
    loading: ({ error }) => {
      if (error) throw error;
      return null;
    },
  }
);

export const UploadSessionScheduleModal = () => {
  const isOpen = useAtomValue(isUploadSessionScheduleModalOpenAtom);
  const isConfirmingDiscard = useAtomValue(isUploadScheduleUnsavedAlertDialogOpenAtom);
  const isBulkCreating = useAtomValue(isBulkCreatingSessionsAtom);
  // Drafts may survive closing the dialog. Keep their navigation guard mounted.
  useBeforeUnloadWarning();
  if (!isOpen && !isConfirmingDiscard && !isBulkCreating) return null;
  return <UploadSessionScheduleModalImplementation />;
};
